import type { Component } from 'vue'

export type ModuleWithComponent = { default?: Component }
export type ModuleRegistry = Record<string, ModuleWithComponent>

type RegistryOptions = {
  addLazyAliases?: boolean
  suffix?: string
  onlySuffixed?: boolean
}

type RegistrySources = {
  baseComponentModules: ModuleRegistry
  templateComponentModules: ModuleRegistry
  thankYouTemplateModules: ModuleRegistry
  paymentFormTemplates: ModuleRegistry
  pledgeFormTemplates: ModuleRegistry
}

export function pascalCase(segment: string) {
  return segment
    .split(/[-_]/g)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join('')
}

export function toComponentName(path: string) {
  const segments = path
    .replace(/^.*\/components\//, '')
    .replace(/^.*\/payment-form-templates\//, '')
    .replace(/^.*\/pledge-form-templates\//, '')
    .replace(/\.[^.]+$/, '')
    .split('/')
    .filter(Boolean)

  const filename = segments[segments.length - 1]

  if (segments.length > 1 && filename === segments[segments.length - 1]) {
    return pascalCase(filename)
  }

  const cleaned = segments.filter((segment, index) => segment !== 'index' || index === 0)
  return cleaned.map(pascalCase).join('')
}

function addKeyAlias(
  registry: Record<string, Component>,
  key: string,
  component: Component
) {
  if (!key || registry[key]) return
  registry[key] = component
}

export function registerModules(
  modules: ModuleRegistry,
  registry: Record<string, Component>,
  options: RegistryOptions = {}
) {
  for (const [path, mod] of Object.entries(modules)) {
    const component = mod?.default
    if (!component) continue

    const name = toComponentName(path)
    if (!name) continue

    const kebab = name.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase()
    const segments = name.split(/(?=[A-Z])/).filter(Boolean)
    const baseName = segments.length > 0 ? segments.slice(-2).join('') : name
    const baseKebab = baseName.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase()
    const rawFilename = path.split('/').pop()?.replace(/\.[^.]+$/, '')
    const suffix = options.suffix
    const pascalSuffix = suffix ? pascalCase(suffix.replace(/^-+/, '')) : ''

    const directKeys = new Set([name, kebab, baseName, baseKebab])
    if (rawFilename && rawFilename !== 'index') {
      directKeys.add(rawFilename)
    }

    const keys = new Set<string>()

    if (!options.onlySuffixed) {
      for (const key of directKeys) {
        keys.add(key)
      }
    }

    if (suffix) {
      keys.add(`${name}${pascalSuffix}`)
      keys.add(`${kebab}${suffix}`)
      keys.add(`${baseName}${pascalSuffix}`)
      keys.add(`${baseKebab}${suffix}`)

      if (rawFilename && rawFilename !== 'index') {
        keys.add(`${rawFilename}${suffix}`)
      }
    }

    for (const key of keys) {
      addKeyAlias(registry, key, component)
    }

    if (!options.addLazyAliases) continue

    for (const key of [name, baseName]) {
      addKeyAlias(registry, `Lazy${key}`, component)
    }

    for (const key of [kebab, baseKebab]) {
      addKeyAlias(registry, `lazy-${key}`, component)
    }

    if (rawFilename && rawFilename !== 'index') {
      addKeyAlias(registry, `lazy-${rawFilename}`, component)
    }
  }
}

export function buildComponentRegistry({
  baseComponentModules,
  templateComponentModules,
  thankYouTemplateModules,
  paymentFormTemplates,
  pledgeFormTemplates
}: RegistrySources) {
  const registry: Record<string, Component> = {}

  registerModules(baseComponentModules, registry, { addLazyAliases: true })
  registerModules(templateComponentModules, registry, { addLazyAliases: true })
  registerModules(thankYouTemplateModules, registry, {
    suffix: '-thank-you',
    onlySuffixed: true
  })
  registerModules(paymentFormTemplates, registry)
  registerModules(pledgeFormTemplates, registry)

  return registry
}
